import { useEffect, useState } from "react";
import { Copy, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, fmtWhen } from "@/lib/api";
import { ApiGuideButton } from "@/components/ApiGuideButton";

const SAMPLE = `curl -X POST "$PORTAL/api/compliance/ingest" \\
  -H "X-API-Key: $SAIL_COMPLIANCE_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"readings": [
        {"code": "DX-BSL-004", "date": "2026-10-07", "pct": 92,
         "kpi_value": 556.2, "impact_lakh": 19.0, "remark": "A shift average"},
        {"code": "DX-BSL-004", "date": "2026-10-08", "pct": 88,
         "kpi_value": 561.4, "impact_lakh": 14.5}
      ]}'`;

/**
 * Automated entry — a plant system posting its own daily compliance.
 *
 * The key is issued per unit and is shown once. A posted reading lands in the same two collections
 * the screens write, so it is on the Dashboard odometer the moment it arrives.
 */
export const ApiKeysPanel = ({ units = [], isAdmin }) => {
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("");
  const [plant, setPlant] = useState("");
  const [fresh, setFresh] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!isAdmin) return;
    api
      .get("/compliance/keys")
      .then((r) => setKeys(r.data.keys))
      .catch(() => setKeys([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [isAdmin]);

  const issue = async () => {
    if (!name.trim()) {
      toast.error("Name the system this key is for — e.g. “BSL Level-2 compliance feed”.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/keys", {
        name,
        plant: plant === "ALL" ? "" : plant,
      });
      setFresh(data.key);
      setName("");
      toast.success("Key issued. Copy it now — the portal keeps only its fingerprint.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/compliance/keys/${id}`);
      toast.success("Revoked.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="api-keys-panel">
      <p className="max-w-3xl text-sm text-muted-foreground">
        A plant system can post its own daily compliance instead of anybody typing it. It writes
        into the very same store this screen does, so a reading that arrives at 06:00 is on the
        Dashboard odometer at 06:00. The usecase has to be on compliance watch first — the portal
        will not invent a KPI it has never been told about.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <ApiGuideButton />
        <span className="text-[11px] text-muted-foreground">
          A short note and step-by-step tutorial to share with a plant or unit that wants to push
          its compliance to CDTP — this portal's own address is already filled in.
        </span>
      </div>

      <div className="mt-4 grid gap-px bg-border lg:grid-cols-2">
        <div className="bg-card p-4">
          <div className="label-caps">How a system posts a day</div>
          <pre
            className="font-data mt-2 overflow-x-auto border border-border bg-muted/50 p-3 text-[10.5px] leading-relaxed"
            data-testid="api-sample"
          >
            {SAMPLE}
          </pre>
          <dl className="mt-3 space-y-1 text-[11px] text-muted-foreground">
            <div>
              <b className="text-foreground">code</b> — the usecase's own code on the register.
            </div>
            <div>
              <b className="text-foreground">date</b> — yyyy-mm-dd or dd.mm.yyyy.
            </div>
            <div>
              <b className="text-foreground">pct</b> — that day's compliance per cent (92, not
              0.92). Leave it out and the day reads N/A, never nought.
            </div>
            <div>
              <b className="text-foreground">kpi_value</b> — the KPI as it actually read that day.
            </div>
            <div>
              <b className="text-foreground">impact_lakh</b> — that day's rupee impact in lakh.
            </div>
            <div>
              Up to 500 days in one call. A day already held is corrected. The reply names every
              day saved and every row refused, with the reason.
            </div>
          </dl>
        </div>

        <div className="bg-card p-4">
          <div className="label-caps">Keys in use</div>
          {!isAdmin ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Keys are issued by an SDTD administrator. Ask SDTD for one for your unit's system and
              the two lines above are all your team needs.
            </p>
          ) : (
            <>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="min-w-[200px] flex-1">
                  <span className="font-data block text-[10px] text-muted-foreground">
                    Which system
                  </span>
                  <Input
                    data-testid="api-key-name"
                    value={name}
                    placeholder="BSL Level-2 compliance feed"
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 h-10 rounded-none"
                  />
                </label>
                <label className="w-[170px]">
                  <span className="font-data block text-[10px] text-muted-foreground">
                    For which unit
                  </span>
                  <Select value={plant} onValueChange={setPlant}>
                    <SelectTrigger className="mt-1 h-10 rounded-none" data-testid="api-key-plant">
                      <SelectValue placeholder="Pick a unit" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72 rounded-none bg-popover">
                      <SelectItem value="ALL">Every unit</SelectItem>
                      {units.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <button
                  type="button"
                  data-testid="api-key-issue"
                  disabled={busy}
                  onClick={issue}
                  className="flex h-10 items-center gap-2 bg-primary px-4 font-display text-[10px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  Issue
                </button>
              </div>

              {fresh && (
                <div
                  className="mt-3 border border-[hsl(var(--watch))] bg-[hsl(var(--watch)/0.08)] p-3"
                  data-testid="api-key-fresh"
                >
                  <div className="label-caps text-[hsl(var(--watch))]">
                    Copy it now — it is never shown again
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="font-data flex-1 break-all text-[11px]">{fresh}</code>
                    <button
                      type="button"
                      data-testid="api-key-copy"
                      onClick={() => {
                        navigator.clipboard?.writeText(fresh);
                        toast.success("Copied.");
                      }}
                      className="border border-border px-2 py-1.5 transition-colors duration-200 hover:bg-muted"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}

              <ul className="mt-3 divide-y divide-border border border-border">
                {(keys || []).map((k) => (
                  <li
                    key={k.id}
                    className="flex flex-wrap items-center gap-3 px-3 py-2 text-[11px]"
                    data-testid={`api-key-${k.id}`}
                  >
                    <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold">{k.name}</span>
                      <span className="font-data block text-[10px] text-muted-foreground">
                        {k.prefix}… · {k.plant || "every unit"} · {k.calls || 0} call(s) ·{" "}
                        {k.last_used_at ? `last used ${fmtWhen(k.last_used_at)}` : "never used"}
                      </span>
                    </span>
                    {k.revoked ? (
                      <span className="font-data text-[10px] uppercase text-muted-foreground">
                        revoked
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`api-key-revoke-${k.id}`}
                        onClick={() => revoke(k.id)}
                        className="text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--at-risk))]"
                        title="Revoke this key"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </li>
                ))}
                {keys && !keys.length && (
                  <li className="px-3 py-4 text-[11px] text-muted-foreground">
                    No key has been issued yet.
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
